import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { ArrowDownUp, DollarSign, Loader2, TrendingUp, TrendingDown, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_V2 = process.env.REACT_APP_BACKEND_URL + '/api/v2';

const COIN_COLORS = {
  BTC: '#F7931A', ETH: '#627EEA', SOL: '#9945FF', LTC: '#345D9D',
  DOGE: '#C2A633', BCH: '#8DC351', XRP: '#23292F', SHIB: '#FF2D00'
};

const fmtPrice = (p) => {
  if (!p || p === 0) return '$0.00';
  if (p >= 100) return `$${p.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  if (p >= 1) return `$${p.toFixed(4)}`;
  if (p >= 0.001) return `$${p.toFixed(6)}`;
  return `$${p.toFixed(8)}`;
};

const ManualTradeModal = ({ open, onOpenChange, onTradeComplete }) => {
  const [tab, setTab] = useState('BUY');
  const [coins, setCoins] = useState([]);
  const [selectedPair, setSelectedPair] = useState('');
  const [usdBalance, setUsdBalance] = useState(0);
  const [amount, setAmount] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [mode, setMode] = useState('simulation');
  const [confirmStep, setConfirmStep] = useState(false);

  const fetchBalances = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_V2}/user/bot/manual-trade/balances`, { withCredentials: true });
      if (res.data.success) {
        setCoins(res.data.coins);
        setUsdBalance(res.data.usd_balance);
        setMode(res.data.mode);
        if (!selectedPair && res.data.coins.length > 0) {
          setSelectedPair(res.data.coins[0].pair);
        }
      }
    } catch (err) {
      console.error('Failed to fetch balances:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedPair]);

  useEffect(() => {
    if (open) {
      fetchBalances();
      setAmount('');
      setConfirmStep(false);
    }
  }, [open, fetchBalances]);

  // Auto-refresh prices every 10s
  useEffect(() => {
    if (!open) return;
    const interval = setInterval(fetchBalances, 5000);
    return () => clearInterval(interval);
  }, [open, fetchBalances]);

  const selected = coins.find(c => c.pair === selectedPair);
  const price = selected?.price || 0;
  const coinBalance = selected?.balance || 0;
  const symbol = selected?.symbol || '';
  const numAmount = parseFloat(amount) || 0;

  // Calculate preview
  const isBuy = tab === 'BUY';
  const coinYouGet = isBuy && price > 0 ? numAmount / price : 0;
  const usdYouGet = !isBuy ? numAmount * price : 0;
  const canExecute = numAmount > 0 && price > 0 && (
    isBuy ? numAmount <= usdBalance : numAmount <= coinBalance
  );

  const handleSetMax = () => {
    if (isBuy) {
      setAmount(usdBalance.toString());
    } else {
      setAmount(coinBalance.toString());
    }
  };

  const handleSetPercent = (pct) => {
    if (isBuy) {
      setAmount((usdBalance * pct / 100).toFixed(2));
    } else {
      setAmount((coinBalance * pct / 100).toFixed(8));
    }
  };

  const handleSubmit = async () => {
    if (!confirmStep) {
      setConfirmStep(true);
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        action: tab,
        pair: selectedPair,
        amount: numAmount
      };
      const res = await axios.post(`${API_V2}/user/bot/manual-trade`, payload, { withCredentials: true });
      if (res.data.success) {
        const d = res.data;
        if (isBuy) {
          toast.success(`Bought ${d.coin_amount?.toFixed(8)} ${symbol} @ ${fmtPrice(d.price)}`);
        } else {
          const profitMsg = d.profit >= 0 ? `+$${d.profit.toFixed(4)}` : `-$${Math.abs(d.profit).toFixed(4)}`;
          toast.success(`Sold ${d.coin_sold?.toFixed(8)} ${symbol} for ${fmtPrice(d.usd_received)} (${profitMsg})`);
        }
        setAmount('');
        setConfirmStep(false);
        fetchBalances();
        onTradeComplete?.();
      }
    } catch (err) {
      const msg = err.response?.data?.detail || 'Trade failed';
      toast.error(msg);
      setConfirmStep(false);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0a0a0a] border-[#003B00] text-[#00FF41] max-w-md p-0 gap-0">
        <DialogHeader className="p-4 pb-2 border-b border-[#003B00]">
          <DialogTitle className="flex items-center gap-2 text-[#00FF41] tracking-wider">
            <ArrowDownUp className="h-5 w-5" /> MANUAL TRADE
            <Badge className={`ml-auto text-[10px] ${mode === 'live' ? 'bg-red-900 text-red-300' : 'bg-blue-900 text-blue-300'}`}>
              {mode.toUpperCase()}
            </Badge>
          </DialogTitle>
        </DialogHeader>

        {loading && coins.length === 0 ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 text-[#00FF41] animate-spin" />
          </div>
        ) : (
          <div className="p-4 space-y-4">
            {/* Buy/Sell Tabs */}
            <div className="grid grid-cols-2 gap-0" data-testid="trade-tabs">
              <button
                onClick={() => { setTab('BUY'); setAmount(''); setConfirmStep(false); }}
                className={`py-2.5 text-sm font-bold tracking-wider transition-colors ${
                  tab === 'BUY'
                    ? 'bg-green-900/50 text-green-400 border border-green-600'
                    : 'bg-[#001a00] text-gray-500 border border-[#003B00] hover:text-gray-400'
                }`}
                data-testid="tab-buy"
              >
                BUY
              </button>
              <button
                onClick={() => { setTab('SELL'); setAmount(''); setConfirmStep(false); }}
                className={`py-2.5 text-sm font-bold tracking-wider transition-colors ${
                  tab === 'SELL'
                    ? 'bg-red-900/50 text-red-400 border border-red-600'
                    : 'bg-[#001a00] text-gray-500 border border-[#003B00] hover:text-gray-400'
                }`}
                data-testid="tab-sell"
              >
                SELL
              </button>
            </div>

            {/* Coin Selector */}
            <div>
              <label className="text-[10px] text-gray-500 uppercase tracking-wider mb-1 block">Select Coin</label>
              <div className="grid grid-cols-3 gap-1.5">
                {coins.map(c => (
                  <button
                    key={c.pair}
                    onClick={() => { setSelectedPair(c.pair); setAmount(''); setConfirmStep(false); }}
                    className={`p-2 border text-xs font-mono flex items-center gap-1.5 transition-all ${
                      selectedPair === c.pair
                        ? 'border-[#00FF41] bg-[#003B00]/40'
                        : 'border-[#003B00]/50 hover:border-[#003B00]'
                    }`}
                    data-testid={`coin-select-${c.symbol}`}
                  >
                    <div className="w-4 h-4 rounded-full flex-shrink-0" style={{ backgroundColor: COIN_COLORS[c.symbol] || '#444' }} />
                    <span className={selectedPair === c.pair ? 'text-[#00FF41]' : 'text-gray-400'}>{c.symbol}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Price & Balance */}
            {selected && (
              <div className="grid grid-cols-2 gap-2">
                <div className="bg-[#001a00] border border-[#003B00]/50 p-2.5">
                  <div className="text-[10px] text-gray-500 uppercase">Price</div>
                  <div className="text-sm font-mono text-[#00FF41] font-bold" data-testid="current-price">{fmtPrice(price)}</div>
                </div>
                <div className="bg-[#001a00] border border-[#003B00]/50 p-2.5">
                  <div className="text-[10px] text-gray-500 uppercase">{isBuy ? 'USD Balance' : `${symbol} Balance`}</div>
                  <div className="text-sm font-mono text-[#00FF41] font-bold" data-testid="available-balance">
                    {isBuy ? `$${usdBalance.toFixed(2)}` : coinBalance.toFixed(8)}
                  </div>
                </div>
              </div>
            )}

            {/* Amount Input */}
            <div>
              <label className="text-[10px] text-gray-500 uppercase tracking-wider mb-1 block">
                {isBuy ? 'USD Amount to Spend' : `${symbol} Amount to Sell`}
              </label>
              <div className="relative">
                <Input
                  type="number"
                  step="any"
                  min="0"
                  value={amount}
                  onChange={(e) => { setAmount(e.target.value); setConfirmStep(false); }}
                  placeholder={isBuy ? '0.00' : '0.00000000'}
                  className="bg-black border-[#003B00] text-[#00FF41] font-mono pr-16"
                  data-testid="trade-amount-input"
                />
                <button
                  onClick={handleSetMax}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-[#008F11] hover:text-[#00FF41] border border-[#003B00] px-2 py-0.5"
                >
                  MAX
                </button>
              </div>
              <div className="flex gap-1.5 mt-1.5">
                {[10, 25, 50, 75, 100].map(pct => (
                  <button
                    key={pct}
                    onClick={() => handleSetPercent(pct)}
                    className="flex-1 text-[10px] text-gray-500 hover:text-[#00FF41] border border-[#003B00]/50 hover:border-[#003B00] py-1 transition-colors"
                  >
                    {pct}%
                  </button>
                ))}
              </div>
            </div>

            {/* Preview */}
            {numAmount > 0 && price > 0 && (
              <div className="bg-[#001a00] border border-[#003B00] p-3 space-y-1.5" data-testid="trade-preview">
                <div className="text-[10px] text-gray-500 uppercase tracking-wider">Preview</div>
                {isBuy ? (
                  <>
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-400">You spend</span>
                      <span className="font-mono text-[#00FF41]">${numAmount.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-400">You get</span>
                      <span className="font-mono text-green-400 font-bold">{coinYouGet.toFixed(8)} {symbol}</span>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-400">You sell</span>
                      <span className="font-mono text-[#00FF41]">{numAmount.toFixed(8)} {symbol}</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-400">You receive</span>
                      <span className="font-mono text-green-400 font-bold">${usdYouGet.toFixed(4)}</span>
                    </div>
                  </>
                )}
                <div className="flex justify-between text-xs">
                  <span className="text-gray-400">Price</span>
                  <span className="font-mono text-gray-400">{fmtPrice(price)}</span>
                </div>
              </div>
            )}

            {/* Confirm Warning */}
            {confirmStep && (
              <div className={`flex items-center gap-2 p-2.5 border text-xs ${
                mode === 'live' ? 'border-red-600 bg-red-950/30 text-red-300' : 'border-yellow-600 bg-yellow-950/30 text-yellow-300'
              }`} data-testid="confirm-warning">
                <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                <span>
                  {mode === 'live'
                    ? `Confirm LIVE ${tab}: This will execute a real order on your exchange.`
                    : `Confirm ${tab}: This will update your simulated portfolio.`}
                </span>
              </div>
            )}

            {/* Execute Button */}
            <Button
              onClick={handleSubmit}
              disabled={!canExecute || submitting}
              className={`w-full py-3 text-sm font-bold tracking-wider rounded-none ${
                isBuy
                  ? 'bg-green-700 hover:bg-green-600 text-white disabled:bg-green-900/30 disabled:text-green-800'
                  : 'bg-red-700 hover:bg-red-600 text-white disabled:bg-red-900/30 disabled:text-red-800'
              }`}
              data-testid="execute-trade-btn"
            >
              {submitting ? (
                <Loader2 className="h-4 w-4 animate-spin mx-auto" />
              ) : confirmStep ? (
                <>
                  {isBuy ? <TrendingUp className="h-4 w-4 mr-2" /> : <TrendingDown className="h-4 w-4 mr-2" />}
                  CONFIRM {tab} {symbol}
                </>
              ) : (
                <>
                  {isBuy ? <TrendingUp className="h-4 w-4 mr-2" /> : <TrendingDown className="h-4 w-4 mr-2" />}
                  {tab} {symbol}
                </>
              )}
            </Button>

            {!canExecute && numAmount > 0 && (
              <p className="text-red-400 text-[10px] text-center">
                {isBuy && numAmount > usdBalance && `Insufficient USD balance ($${usdBalance.toFixed(2)} available)`}
                {!isBuy && numAmount > coinBalance && `Insufficient ${symbol} balance (${coinBalance.toFixed(8)} available)`}
              </p>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ManualTradeModal;
